import { Booking, MeetingRoom } from '../types/models';

/**
 * 서버 조회가 실패했을 때 마지막으로 성공한 데이터를 대신 보여주기 위한 캐시.
 * 브라우저를 닫았다 열어도 남아야 하므로 localStorage를 사용한다.
 */
const BOOKINGS_CACHE_KEY = 'offline_bookings';
const ROOMS_CACHE_KEY = 'offline_rooms';

export interface CacheEnvelope<T> {
  /** 캐시에 저장한 시각 */
  savedAt: number;
  data: T;
}

const write = <T,>(key: string, data: T) => {
  try {
    const envelope: CacheEnvelope<T> = { savedAt: Date.now(), data };
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch (error) {
    // 저장 공간 부족 등으로 실패해도 앱 동작에는 영향이 없어야 한다
    console.warn(`오프라인 캐시 저장 실패 (${key}):`, error);
  }
};

const read = <T,>(key: string): CacheEnvelope<T> | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number' || !Array.isArray(parsed.data)) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed as CacheEnvelope<T>;
  } catch (error) {
    console.warn(`오프라인 캐시 읽기 실패 (${key}):`, error);
    return null;
  }
};

export const saveBookingsCache = (bookings: Booking[]) => write(BOOKINGS_CACHE_KEY, bookings);

/**
 * 저장된 예약 목록을 읽는다.
 * JSON을 거치며 문자열이 된 날짜를 Date로 되돌려 서버에서 받은 형식과 맞춘다.
 */
export const readBookingsCache = (): CacheEnvelope<Booking[]> | null => {
  const cached = read<any[]>(BOOKINGS_CACHE_KEY);
  if (!cached) {
    return null;
  }

  try {
    return {
      savedAt: cached.savedAt,
      data: cached.data.map(booking => ({
        ...booking,
        startTime: new Date(booking.startTime),
        endTime: new Date(booking.endTime),
        recurrence_end_date: booking.recurrence_end_date
          ? new Date(booking.recurrence_end_date)
          : undefined,
      })) as Booking[],
    };
  } catch (error) {
    console.warn('오프라인 예약 캐시 변환 실패:', error);
    return null;
  }
};

export const saveRoomsCache = (rooms: MeetingRoom[]) => write(ROOMS_CACHE_KEY, rooms);

export const readRoomsCache = (): CacheEnvelope<MeetingRoom[]> | null =>
  read<MeetingRoom[]>(ROOMS_CACHE_KEY);

/**
 * 캐시 저장 시각을 고정된 시각 표현으로 바꾼다. ("오후 2:32")
 *
 * "10분 전" 같은 상대 표현은 화면이 떠 있는 동안 다시 계산되지 않아
 * 시간이 지나면 실제와 어긋난다. 고정 시각은 언제 읽어도 맞다.
 */
export const formatCachedTime = (savedAt: number): string => {
  const saved = new Date(savedAt);
  const now = new Date();

  const time = saved.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' });

  const isSameDay =
    saved.getFullYear() === now.getFullYear() &&
    saved.getMonth() === now.getMonth() &&
    saved.getDate() === now.getDate();

  // 하루가 지났으면 시각만으로는 언제인지 알 수 없으므로 날짜를 붙인다
  return isSameDay ? time : `${saved.getMonth() + 1}월 ${saved.getDate()}일 ${time}`;
};
